
import React from 'react';
import { Building, ExternalLink, Users, Globe, Star, Pencil, Edit } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { createSafeExternalUrl } from "@/utils/urlUtils";
import { useAppSelector } from '@/hooks/useAppSelector';
import { Link } from 'react-router-dom';

interface CompanyHeaderProps {
  company: {
    slug: string; // Only slug is required
  };
  activePage?: string; // Current active page subslug
  onEdit?: () => void;
}

const CompanyHeader: React.FC<CompanyHeaderProps> = ({ 
  company,
  activePage,
  onEdit
}) => {
  // Get GraphQL data from Redux
  const graphQLData = useAppSelector((state) => state.graphql.data);
  
  // Log Redux data access
  console.log('[graphQL Redux] CompanyHeader accessing GraphQL data for:', company.slug);

  // Build display data from GraphQL Redux data
  const displayData = {
    slug: graphQLData?.slug || company.slug,
    description: graphQLData?.description,
    category: graphQLData?.category,
    employees: graphQLData?.employees,
    companyLogoUrl: graphQLData?.logo_url,
    website: graphQLData?.url,
    // Find the active page that matches the activePage parameter, fall back to first page if no match
    pricingPageUrl: (() => {
      if (graphQLData?.pages?.length) {
        // Find the page that matches the active page
        const matchingPage = activePage ? 
          graphQLData.pages.find(page => (page.subslug || 'main') === activePage) : null;
        
        // If found, use its pricing page URL
        if (matchingPage?.pricing_page_url) {
          return matchingPage.pricing_page_url;
        }
        
        // Otherwise, use the first page's pricing page URL
        return graphQLData.pages[0]?.pricing_page_url || `https://${company.slug}.com/pricing`;
      }
      
      // Fallback if no pages are available
      return `https://${company.slug}.com/pricing`;
    })(),
  };
    
  console.log('🔗 [CompanyHeader] Using pricing page URL:', displayData.pricingPageUrl, 'for company:', displayData.slug);

  return (
    <div className="flex flex-col space-y-4 mb-6">
      <div className="flex items-center">
        {displayData.companyLogoUrl ? (
          <div className="h-12 w-12 mr-4 flex-shrink-0">
            <img 
              src={displayData.companyLogoUrl} 
              alt={`${displayData.slug} logo`} 
              className="h-full w-full object-contain"
            />
          </div>
        ) : (
          <div className="h-12 w-12 mr-4 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center">
            <Building className="h-6 w-6 text-gray-400" />
          </div>
        )}
        
        <div className="flex-1">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold capitalize">
              {displayData.slug.replace(/-/g, ' ')}
            </h1>
            {onEdit && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onEdit} 
                className="ml-2 p-1 h-auto"
                title="Edit Company"
              >
                <Pencil className="h-4 w-4 text-gray-500 hover:text-gray-700" />
              </Button>
            )}
          </div>
          {displayData.description && (
            <p className="text-sm text-gray-600 mt-1">{displayData.description}</p>
          )}
        </div>
        
      </div>
      
      {/* Edit Company Link */}
      <div className="mb-4">
        <Link to={`/edit/${displayData.slug}`}>
          <Button variant="outline" size="sm" className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            Edit Company Details
          </Button>
        </Link>
      </div>
      
      <div className="flex flex-wrap items-center gap-2">
        {displayData.category && (
          <Badge variant="outline" className="flex items-center space-x-1">
            <Star className="h-3 w-3 text-gray-500" />
            <span>{displayData.category}</span>
          </Badge>
        )}
        {displayData.employees && (
          <Badge variant="outline" className="flex items-center space-x-1">
            <Users className="h-3 w-3 text-gray-500" />
            <span>{displayData.employees}</span>
          </Badge>
        )}
        {displayData.pricingPageUrl && (
          <a 
            href={createSafeExternalUrl(displayData.pricingPageUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Visit Pricing Page
          </a>
        )}
      </div>
    </div>
  );
};

// Loading skeleton for the header
export const CompanyHeaderSkeleton = () => (
  <div className="flex flex-col space-y-4 mb-6">
    <div className="flex items-center">
      <Skeleton className="h-12 w-12 mr-4 rounded-md" />
      <div className="flex-1">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-full max-w-md" />
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Skeleton className="h-6 w-24 rounded-full" />
      <Skeleton className="h-6 w-32 rounded-full" />
    </div>
  </div>
);

export default CompanyHeader;
